import type { BetHistoryEntity, BetHistoryEntitySelection, TransStatement } from '@/common/api/model';
import { TransactionType } from '@/common/config';
import {
  TRANSACTION_STATUS_NAME_ENUM,
  isPaymentTransaction,
  isBetTransactionType,
  type TransactionStatus,
  TRANSACTION_TYPE_NAME_ENUM,
} from '@/common/transaction-utils';
import { formatMoney } from '@/common/utils';
import { tConversation } from '@/plugins/i18n';

export interface TransactionDisplayData {
  id: string;
  title: string;
  date: string | Date | number;
  status: string;
  amount: number | string;
}

/**
 * Status mapping for bet winning status
 * Maps bet winningStatus to display text
 */
const getWinningStatusMap = (): Record<number, string> => ({
  0: tConversation('page.issue.bet.running') as string,
  5: tConversation('page.issue.bet.partial_win') as string,
  20: tConversation('page.issue.bet.won') as string,
  30: tConversation('page.issue.bet.lost') as string,
  40: tConversation('page.issue.bet.void') as string,
  90: tConversation('page.issue.bet.pending') as string,
});

/**
 * Status mapping for bet selection/user events status
 * Maps selection status to display text
 */
const getUserSelectionEventsStatusMap = (): Record<number, string> => ({
  0: tConversation('page.issue.bet.running') as string,
  1: tConversation('page.issue.bet.won') as string,
  2: tConversation('page.issue.bet.lost') as string,
  3: tConversation('page.issue.bet.cancelled') as string,
  4: tConversation('page.issue.bet.void') as string,
  5: tConversation('page.issue.bet.win_and_refund_half') as string,
  6: tConversation('page.issue.bet.lost_and_refund_half') as string,
});

/**
 * Composable for transaction display logic
 * Provides functions to extract display data from different transaction types
 */
export function useTransactionDisplay() {
  /**
   * Get display data for a payment transaction (deposit/withdrawal)
   */
  const getPaymentTransactionDisplay = (tx: TransStatement, type: TransactionType): TransactionDisplayData => {
    const authority = tx.counterAuthority || tx.counterFull || null;
    const base = `${TRANSACTION_TYPE_NAME_ENUM[type]} of ${formatMoney(tx.amount)}`;
    const title = authority ? `${base} (from "${authority}")` : base;

    return {
      id: tx.tradeId,
      title,
      date: tx.createTime,
      status: TRANSACTION_STATUS_NAME_ENUM[tx.status as TransactionStatus] || 'Unknown',
      amount: formatMoney(tx.amount),
    };
  };

  /**
   * Get display data for a bet transaction
   */
  const getBetTransactionDisplay = (tx: BetHistoryEntity): TransactionDisplayData => {
    // Generate title based on first selection
    let title = '';
    if (tx.selections && tx.selections.length > 0) {
      const firstSelection = tx.selections[0];
      const isOutright = firstSelection.eventId.includes('match');
      const eventTitle = isOutright
        ? `${firstSelection.home} vs ${firstSelection.away}`
        : firstSelection.tournamentName || '';

      // Add "and X more" if there are multiple selections
      const moreText = tx.selections.length > 1 ? ` and ${tx.selections.length - 1} more` : '';

      // Format: "Event Title and X more (Id: shortId)"
      title = `${eventTitle}${moreText} (Id: ${tx.shortId})`;
    } else {
      title = `(Id: ${tx.shortId})`;
    }

    // Use winningStatus for bet transaction status
    const winningStatusMap = getWinningStatusMap();
    const status = winningStatusMap[tx.winningStatus] || 'Unknown';

    return {
      id: tx.orderId,
      title,
      date: tx.createTime,
      status,
      amount: Number(tx.totalStake),
    };
  };

  /**
   * Get display data for a bet selection (user event)
   * Used when displaying individual selections within a bet
   */
  const getBetSelectionDisplay = (
    selection: BetHistoryEntitySelection,
    parentBet: BetHistoryEntity
  ): TransactionDisplayData => {
    // Generate title based on selection (same format as bet)
    const isOutright = selection.eventId.includes('match');
    const eventTitle = isOutright ? `${selection.home} vs ${selection.away}` : selection.tournamentName || '';

    // Format: "Event Title (Id: eventId)"
    const title = `${eventTitle} (Id: ${selection.eventId})`;

    // Use selection status for user event status
    const selectionStatusMap = getUserSelectionEventsStatusMap();
    const status = selectionStatusMap[selection.status] || 'Unknown';

    // For amount, we can show odds or use parent bet stake
    const amount = selection.odds || Number(parentBet.totalStake);

    return {
      id: selection.id.toString(),
      title,
      date: parentBet.createTime, // Use parent bet's create time
      status,
      amount: Number(amount),
    };
  };

  /**
   * Generic function that handles any transaction type
   */
  const getTransactionDisplay = (
    tx: TransStatement | BetHistoryEntity,
    type: TransactionType
  ): TransactionDisplayData => {
    if (isPaymentTransaction(tx)) {
      return getPaymentTransactionDisplay(tx, type);
    } else if (isBetTransactionType(tx)) {
      return getBetTransactionDisplay(tx);
    }

    // Fallback
    return {
      id: 'unknown',
      title: 'Unknown Transaction',
      date: new Date(),
      status: 'Unknown',
      amount: 0,
    };
  };

  return {
    getPaymentTransactionDisplay,
    getBetTransactionDisplay,
    getBetSelectionDisplay,
    getTransactionDisplay,
  };
}
